import React, { useState, useEffect } from 'react';
import { Plus, Eye, Check, X, Lock } from '../icons';
import { styles, Modal } from '../theme';
import { API_URL, fetchWithAuth } from '../api';
import { usePermissions } from '../context/PermissionContext';
import { formatPHDatetime } from '../utils';

const REQUEST_TYPES = {
  general: { label: 'General Request' },
  revert_cancelled_cheque: { label: 'Cheque Monitoring: Revert Cancelled Cheque to Unused', module: 'cheques', action_type: 'revert_cancelled_to_unused' },
  unlock_account: { label: 'User Account: Unlock Locked Account', module: 'users', action_type: 'unlock_account' }
};

const ticketTypeLabel = (ticket) => {
  if (ticket.ticket_type === 'adjustment' && ticket.module === 'cheques' && ticket.action_type === 'revert_cancelled_to_unused') {
    return 'Cheque Reactivation';
  }
  if (ticket.ticket_type === 'adjustment' && ticket.module === 'users' && ticket.action_type === 'unlock_account') {
    return 'Account Unlock';
  }
  return 'General';
};

const isUnlockTicket = (ticket) =>
  ticket && ticket.module === 'users' && ticket.action_type === 'unlock_account';

const priorityBadgeStyle = (priority) => {
  switch (priority) {
    case 'urgent': return { ...styles.badge, backgroundColor: '#7f1d1d', color: 'white' };
    case 'high': return { ...styles.badge, ...styles.badgeRed };
    case 'low': return { ...styles.badge, ...styles.badgeBlue };
    default: return { ...styles.badge, ...styles.badgeYellow };
  }
};

const statusBadgeStyle = (status) => {
  switch (status) {
    case 'resolved': return { ...styles.badge, ...styles.badgeGreen };
    case 'in_progress': return { ...styles.badge, ...styles.badgeBlue };
    case 'closed': return { ...styles.badge, backgroundColor: '#e5e7eb', color: '#374151' };
    default: return { ...styles.badge, ...styles.badgeYellow };
  }
};

const STATUS_TABS = [
  { value: '', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' }
];

const emptyForm = {
  request_type: 'general',
  title: '',
  description: '',
  priority: 'medium',
  reference_id: ''
};

const Helpdesk = ({ token }) => {
  const { canCreate, canApprove } = usePermissions();
  const [currentUser] = useState(JSON.parse(localStorage.getItem('user') || '{}'));
  const [tickets, setTickets] = useState([]);
  const [cancelledCheques, setCancelledCheques] = useState([]);
  const [lockedUsers, setLockedUsers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [viewingTicket, setViewingTicket] = useState(null);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/helpdesk-tickets`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setTickets(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching helpdesk tickets:', err);
    }
  };

  const fetchCancelledCheques = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/cheque-book-items?status=cancelled&per_page=200`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setCancelledCheques(data.cheques || []);
    } catch (err) {
      console.error('Error fetching cancelled cheques:', err);
    }
  };

  const fetchLockedUsers = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/helpdesk/locked-users`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setLockedUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching locked users:', err);
    }
  };

  const openCreateForm = () => {
    setForm(emptyForm);
    setShowForm(true);
  };

  const handleRequestTypeChange = (requestType) => {
    setForm({ ...emptyForm, request_type: requestType });
    if (requestType === 'revert_cancelled_cheque') {
      fetchCancelledCheques();
    } else if (requestType === 'unlock_account') {
      fetchLockedUsers();
    }
  };

  const handleChequeSelect = (chequeId) => {
    const cheque = cancelledCheques.find(c => String(c.id) === String(chequeId));
    setForm({
      ...form,
      reference_id: chequeId,
      title: cheque ? `Revert Cancelled Cheque ${cheque.cheque_number} to Unused` : form.title
    });
  };

  const handleLockedUserSelect = (userId) => {
    const user = lockedUsers.find(u => String(u.id) === String(userId));
    setForm({
      ...form,
      reference_id: userId,
      title: user ? `Unlock Account: ${user.username}` : form.title
    });
  };

  const handleSubmit = async () => {
    if (!form.title.trim() || !form.description.trim()) {
      alert('Title and Description are required');
      return;
    }

    const typeMeta = REQUEST_TYPES[form.request_type];
    let payload = {
      title: form.title,
      description: form.description,
      priority: form.priority
    };

    // Any request type carrying module metadata becomes an adjustment ticket
    // that an approver must action (cheque reactivation, account unlock, ...).
    if (typeMeta && typeMeta.module) {
      if (!form.reference_id) {
        alert(form.request_type === 'unlock_account'
          ? 'Please select the locked account to unlock'
          : 'Please select a cancelled cheque to revert');
        return;
      }
      payload = {
        ...payload,
        ticket_type: 'adjustment',
        module: typeMeta.module,
        action_type: typeMeta.action_type,
        reference_id: form.reference_id
      };
    }

    try {
      const res = await fetchWithAuth(`${API_URL}/helpdesk-tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        setShowForm(false);
        fetchTickets();
      } else {
        const errData = await res.json();
        alert('Error: ' + (errData.error || JSON.stringify(errData)));
      }
    } catch (err) {
      console.error('Error creating helpdesk ticket:', err);
    }
  };

  const handleApprove = async (id) => {
    if (!window.confirm('Approve this request? This will apply the change immediately.')) return;
    try {
      const res = await fetchWithAuth(`${API_URL}/helpdesk-tickets/${id}/approve`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        fetchTickets();
      } else {
        alert('Error: ' + (data.error || 'Unable to approve request'));
      }
    } catch (err) {
      console.error('Error approving ticket:', err);
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Reject this request?')) return;
    try {
      const res = await fetchWithAuth(`${API_URL}/helpdesk-tickets/${id}/reject`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        fetchTickets();
      } else {
        alert('Error: ' + (data.error || 'Unable to reject request'));
      }
    } catch (err) {
      console.error('Error rejecting ticket:', err);
    }
  };

  const visibleTickets = tickets.filter(t => {
    if (statusFilter && t.status !== statusFilter) return false;
    if (searchQuery && !t.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const getTabStyle = (targetStatus) => ({
    padding: '8px 16px',
    borderRadius: '8px',
    cursor: 'pointer',
    border: '2px solid',
    borderColor: statusFilter === targetStatus ? '#4f46e5' : 'transparent',
    backgroundColor: statusFilter === targetStatus ? '#eef2ff' : '#f3f4f6',
    color: statusFilter === targetStatus ? '#4338ca' : '#374151',
    fontSize: '14px',
    fontWeight: '500'
  });

  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.title}>Helpdesk</h1>
        {canCreate('helpdesk') && (
          <button onClick={openCreateForm} style={styles.button}>
            <Plus style={{ width: '20px', height: '20px' }} /> New Ticket
          </button>
        )}
      </div>

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title="New Helpdesk Ticket" maxWidth="600px">
        <div style={styles.formGroup}>
          <label style={styles.label}>Request Type</label>
          <select value={form.request_type} onChange={(e) => handleRequestTypeChange(e.target.value)} style={styles.input}>
            {Object.entries(REQUEST_TYPES).map(([key, meta]) => (
              <option key={key} value={key}>{meta.label}</option>
            ))}
          </select>
        </div>

        {form.request_type === 'revert_cancelled_cheque' && (
          <div style={styles.formGroup}>
            <label style={styles.label}>Cancelled Cheque *</label>
            <select value={form.reference_id} onChange={(e) => handleChequeSelect(e.target.value)} style={styles.input}>
              <option value="">{cancelledCheques.length ? 'Select cheque...' : 'No cancelled cheques found'}</option>
              {cancelledCheques.map(c => (
                <option key={c.id} value={c.id}>{c.cheque_number} {c.payee ? `- ${c.payee}` : ''}</option>
              ))}
            </select>
          </div>
        )}

        {form.request_type === 'unlock_account' && (
          <div style={styles.formGroup}>
            <label style={styles.label}>Locked Account *</label>
            <select value={form.reference_id} onChange={(e) => handleLockedUserSelect(e.target.value)} style={styles.input}>
              <option value="">{lockedUsers.length ? 'Select locked account...' : 'No locked accounts found'}</option>
              {lockedUsers.map(u => (
                <option key={u.id} value={u.id}>{u.username}{u.fullname ? ` - ${u.fullname}` : ''}</option>
              ))}
            </select>
            <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '6px', marginBottom: 0 }}>
              Locked users cannot sign in, so this request is raised on their behalf.
            </p>
          </div>
        )}

        <div style={styles.formGroup}>
          <label style={styles.label}>Title *</label>
          <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} style={styles.input} />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Description *</label>
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} style={{ ...styles.input, minHeight: '90px' }}></textarea>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Priority</label>
          <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} style={styles.input}>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>

        <div style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button onClick={() => setShowForm(false)} style={{ ...styles.button, ...styles.buttonSecondary }}>Cancel</button>
          <button onClick={handleSubmit} style={styles.button}>Submit Ticket</button>
        </div>
      </Modal>

      <Modal isOpen={!!viewingTicket} onClose={() => setViewingTicket(null)} title={`Ticket #${viewingTicket?.id}`} maxWidth="600px">
        {viewingTicket && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px', marginBottom: '20px', backgroundColor: '#f9fafb', padding: '16px', borderRadius: '8px' }}>
              <div>
                <p style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', marginBottom: '4px' }}>Type</p>
                <p style={{ fontWeight: '500' }}>{ticketTypeLabel(viewingTicket)}</p>
              </div>
              <div>
                <p style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', marginBottom: '4px' }}>Priority</p>
                <span style={priorityBadgeStyle(viewingTicket.priority)}>{viewingTicket.priority?.toUpperCase()}</span>
              </div>
              <div>
                <p style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', marginBottom: '4px' }}>Status</p>
                <span style={statusBadgeStyle(viewingTicket.status)}>{viewingTicket.status?.replace('_', ' ').toUpperCase()}</span>
              </div>
              <div>
                <p style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', marginBottom: '4px' }}>Requested By</p>
                <p style={{ fontWeight: '500' }}>{viewingTicket.created_by_name || '-'}</p>
                <p style={{ fontSize: '11px', color: '#6b7280' }}>{formatPHDatetime(viewingTicket.created_at)}</p>
              </div>
              {viewingTicket.approved_by_name && (
                <div>
                  <p style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', marginBottom: '4px' }}>{viewingTicket.approval_status === 'rejected' ? 'Rejected By' : 'Approved By'}</p>
                  <p style={{ fontWeight: '500' }}>{viewingTicket.approved_by_name}</p>
                  <p style={{ fontSize: '11px', color: '#6b7280' }}>{formatPHDatetime(viewingTicket.approved_at)}</p>
                </div>
              )}
            </div>
            <div style={{ marginBottom: '8px' }}>
              <p style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', marginBottom: '4px' }}>Description</p>
              <p style={{ whiteSpace: 'pre-wrap' }}>{viewingTicket.description}</p>
            </div>

            {isUnlockTicket(viewingTicket) && (
              <div style={{
                marginTop: '16px', padding: '14px 16px', borderRadius: '8px',
                backgroundColor: '#fef2f2', border: '1px solid #fecaca'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                  <Lock style={{ width: 16, height: 16, color: '#b91c1c' }} />
                  <strong style={{ color: '#b91c1c' }}>Why this account was locked</strong>
                </div>

                {viewingTicket.lock_info?.account && (
                  <p style={{ fontSize: '13px', margin: '0 0 8px 0' }}>
                    <strong>{viewingTicket.lock_info.account.username}</strong>
                    {viewingTicket.lock_info.account.fullname ? ` (${viewingTicket.lock_info.account.fullname})` : ''}
                    {' — '}
                    <span style={{ color: viewingTicket.lock_info.account.is_active ? '#059669' : '#b91c1c' }}>
                      {viewingTicket.lock_info.account.is_active ? 'Currently active' : 'Currently locked'}
                    </span>
                    {`, ${viewingTicket.lock_info.account.failed_attempts || 0} failed login attempt(s)`}
                  </p>
                )}

                {viewingTicket.lock_info?.reasons?.length > 0 ? (
                  <>
                    <ul style={{ margin: '0 0 8px 0', paddingLeft: '20px', fontSize: '13px' }}>
                      {viewingTicket.lock_info.reasons.map((reason, idx) => (
                        <li key={idx}>{reason}</li>
                      ))}
                    </ul>
                    <p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>
                      Security alert recorded {formatPHDatetime(viewingTicket.lock_info.locked_at)}
                    </p>
                  </>
                ) : (
                  <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>
                    No security alert on record for this account — it may have been deactivated manually by an administrator.
                  </p>
                )}
              </div>
            )}

            <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setViewingTicket(null)} style={styles.button}>Close</button>
            </div>
          </>
        )}
      </Modal>

      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {STATUS_TABS.map(tab => (
          <div key={tab.value} onClick={() => setStatusFilter(tab.value)} style={getTabStyle(tab.value)}>
            {tab.label}
          </div>
        ))}
      </div>

      <div style={{ ...styles.card, marginBottom: '20px' }}>
        <div style={styles.formGroup}>
          <label style={styles.label}>Search by Title</label>
          <input type="text" placeholder="Search tickets..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} style={styles.input} />
        </div>
      </div>

      <div style={{ ...styles.card, padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>ID</th>
              <th style={styles.th}>Title</th>
              <th style={styles.th}>Type</th>
              <th style={styles.th}>Priority</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Requested By</th>
              <th style={styles.th}>Date</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleTickets.map(t => (
              <tr key={t.id}>
                <td style={styles.td}>HD-{t.id}</td>
                <td style={{ ...styles.td, maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</td>
                <td style={styles.td}>{ticketTypeLabel(t)}</td>
                <td style={styles.td}><span style={priorityBadgeStyle(t.priority)}>{t.priority?.toUpperCase()}</span></td>
                <td style={styles.td}><span style={statusBadgeStyle(t.status)}>{t.status?.replace('_', ' ').toUpperCase()}</span></td>
                <td style={styles.td}>{t.created_by_name || '-'}</td>
                <td style={styles.td}>{formatPHDatetime(t.created_at)}</td>
                <td style={{ ...styles.td, textAlign: 'right' }}>
                  <button title="View Details" onClick={() => setViewingTicket(t)} style={{ ...styles.iconButton, color: '#6b7280' }}>
                    <Eye style={{ width: '18px', height: '18px' }} />
                  </button>
                  {t.ticket_type === 'adjustment' && t.approval_status === 'pending' && canApprove('helpdesk') && Number(currentUser.id) !== Number(t.created_by) && (
                    <>
                      <button title="Approve" onClick={() => handleApprove(t.id)} style={{ ...styles.iconButton, color: '#10b981' }}>
                        <Check style={{ width: '18px', height: '18px' }} />
                      </button>
                      <button title="Reject" onClick={() => handleReject(t.id)} style={{ ...styles.iconButton, color: '#ef4444' }}>
                        <X style={{ width: '18px', height: '18px' }} />
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {visibleTickets.length === 0 && (
              <tr><td colSpan="8" style={{ ...styles.td, textAlign: 'center' }}>No tickets found.</td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
};

export default Helpdesk;
